import http from '@/services/http';

const stability = {
  // ─── List ───────────────────────────────────────────────────────────────────

  index(tableFilters, where = {}, config = {}) {
    return http.request({
      method: 'get',
      url: '/stability-configurations',
      params: {
        filter: {
          ...tableFilters,
          where,
        },
      },
      ...config,
    });
  },

  count(where = {}, config = {}) {
    return http.request({
      method: 'get',
      url: '/stability-configurations/count',
      params: { where },
      ...config,
    });
  },

  // ─── Detail ─────────────────────────────────────────────────────────────────

  show(id) {
    // No filter: the API ignores it and returns the whole configuration tree
    // (time points -> matches -> condition, analyses, entries) plus the
    // conditions that no match pairs yet.
    return http.request({
      method: 'get',
      url: `/stability-configurations/${id}`,
    });
  },

  findBySample(sampleId) {
    return http.request({
      method: 'get',
      url: `/stability-configurations/by-sample/${sampleId}`,
    });
  },

  updateConfig(id, data) {
    return http.request({
      method: 'patch',
      url: `/stability-configurations/${id}`,
      data,
    });
  },

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  configure(id) {
    return http.request({
      method: 'post',
      url: `/stability-configurations/${id}/configure`,
    });
  },

  retry(id) {
    return http.request({
      method: 'post',
      url: `/stability-configurations/${id}/retry`,
    });
  },

  // ─── Children ───────────────────────────────────────────────────────────────

  getChildren(id) {
    return http.request({
      method: 'get',
      url: `/stability-configurations/${id}/children`,
    });
  },

  // ─── Time Point CRUD ───────────────────────────────────────────────────────

  createTimePoint(configId, data) {
    return http.request({
      method: 'post',
      url: `/stability-configurations/${configId}/time-points`,
      data,
    });
  },

  updateTimePoint(configId, tpId, data) {
    return http.request({
      method: 'patch',
      url: `/stability-configurations/${configId}/time-points/${tpId}`,
      data,
    });
  },

  deleteTimePoint(configId, tpId) {
    return http.request({
      method: 'delete',
      url: `/stability-configurations/${configId}/time-points/${tpId}`,
    });
  },

  // ─── Time Point File ───────────────────────────────────────────────────────

  // The API accepts any field name (multer .any()) and validates the type by
  // extension. Sent through this client on purpose: the auth token comes from
  // the axios request interceptor, which el-upload's own XHR would bypass.
  // SMP-1647 — adds a file next to the ones the time point already has.
  uploadTimePointFile(configId, tpId, file) {
    const data = new FormData();
    data.append('file', file);
    return http.request({
      method: 'post',
      url: `/stability-configurations/${configId}/time-points/${tpId}/files`,
      headers: { 'Content-Type': 'multipart/form-data' },
      data,
    });
  },

  // Soft on the API side: the file can be restored. `timePointFileId` is the
  // id of the attachment, not of the file row the download uses.
  removeTimePointFile(configId, tpId, timePointFileId) {
    return http.request({
      method: 'delete',
      url: `/stability-configurations/${configId}/time-points/${tpId}/files/${timePointFileId}`,
    });
  },

  // ─── Protocol ──────────────────────────────────────────────────────────────

  // SMP-1624 — one pdf per configuration, replacing the previous one. Through
  // this client for the same reason as uploadTimePointFile.
  uploadProtocol(configId, file) {
    const data = new FormData();
    data.append('file', file);
    return http.request({
      method: 'post',
      url: `/stability-configurations/${configId}/protocol`,
      headers: { 'Content-Type': 'multipart/form-data' },
      data,
    });
  },

  // ─── Condition CRUD ────────────────────────────────────────────────────────

  createCondition(configId, data) {
    return http.request({
      method: 'post',
      url: `/stability-configurations/${configId}/conditions`,
      data,
    });
  },

  // ─── Match CRUD ────────────────────────────────────────────────────────────

  createMatch(configId, data) {
    return http.request({
      method: 'post',
      url: `/stability-configurations/${configId}/matches`,
      data,
    });
  },

  cloneMatch(configId, matchId, data) {
    return http.request({
      method: 'post',
      url: `/stability-configurations/${configId}/matches/${matchId}/clone`,
      data,
    });
  },

  deleteMatch(configId, matchId) {
    return http.request({
      method: 'delete',
      url: `/stability-configurations/${configId}/matches/${matchId}`,
    });
  },

  // ─── Analysis Association ──────────────────────────────────────────────────
  // Both answer with the match, whose toDate the API recomputes from the MAX
  // lead time of its analyses.

  addAnalysis(configId, matchId, data) {
    return http.request({
      method: 'post',
      url: `/stability-configurations/${configId}/matches/${matchId}/analyses`,
      data,
    });
  },

  removeAnalysis(configId, matchId, scaId) {
    return http.request({
      method: 'delete',
      url: `/stability-configurations/${configId}/matches/${matchId}/analyses/${scaId}`,
    });
  },

  // ─── Schedule ─────────────────────────────────────────────────────────────

  getSchedule(fromDate, toDate, filters = {}, config = {}) {
    return http.request({
      method: 'get',
      url: '/stability-configurations/schedule',
      params: {
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        ...filters,
      },
      ...config,
    });
  },

  // ─── Time Point Export ────────────────────────────────────────────────────

  timePointExport(data, config = {}) {
    return http.request({
      method: 'post',
      url: '/stability-time-points/export',
      data,
      responseType: 'blob',
      ...config,
    });
  },

  // ─── Analysis Search ──────────────────────────────────────────────────────

  searchAnalyses(key, value) {
    const filter = {
      where: { [key]: { regexp: `/${value}/i` } },
      limit: 20,
    };
    return http.request({
      method: 'get',
      url: '/stability-configurations/searchable-analyses',
      params: { filter },
    });
  },
};

export default stability;
